<script>
  import { onMount, tick } from 'svelte';
  import { geoPath } from 'd3-geo';
  import { feature } from 'topojson-client';
  import unemployment_data_oct_2020 from '../unemployment_data_oct_2020.json';
  import abbr_to_state from './abbr_to_state.json';
  import state_symbols from './state_symbols.json';
  import bubbles from './state_bubbles.json';
  import Card from './card.svelte';
  import SendHeight from './sender.js';

  ////component state
  //us geo map
  let stateGeo = [];
  //unemployment data US states
  let states = unemployment_data_oct_2020.states;
  let stateNames = Object.keys(states).sort();
  let maxUnemployment = unemployment_data_oct_2020.max;
  let subtitle = unemployment_data_oct_2020.metric;
  let lastUpdated = unemployment_data_oct_2020.lastUpdated;
  //used for dropdowns
  let selectedState = "Alabama";

  //the box for viewing
  const vb = {
    x: 0,
    y: 0,
    w: 975,
    h: 650
  };
  //state for tooltip
  let tooltipText;
  let tooltipLeft = false;
  let tooltipUp = false;
  let tooltipX;
  let tooltipY;
  let tooltipUnemployed = 0;
  let displayTooltip = false;

  //legend
  const numCircles = 10;

  //helpers
  const path = geoPath();

  const mousemove = (e, name) => {
    tooltipText = name;
    tooltipLeft = e.pageX > window.innerWidth / 2;
    tooltipUp = e.clientY > window.innerHeight * 0.666;
    tooltipX = e.pageX + 15;
    tooltipY = e.pageY + 15;
    tooltipUnemployed = `${states[name]}%`;
    displayTooltip = true;
    raise(name);
  };

  const raise = (() => {
    let selected;
    return name => {
      if (name === selected) return;
      else {
        selected = name;
        stateGeo = stateGeo.sort((a,b) => a.properties.name === name ? 1 : -1);
      }
    }
  })();

  const scaledColor = f => {
    let maxR = 0x00;
    let maxG = 0x6c;
    let maxB = 0xc4;
    let r = 0xFF - (0xFF - maxR) * f;
    r = (r | 0).toString("16").padStart(2, "0");
    let g = 0xFF - (0xFF - maxG) * f;
    g = (g | 0).toString("16").padStart(2, "0");
    let b = 0xFF - (0xFF - maxB) * f;
    b = (b | 0).toString("16").padStart(2, "0");
    return `#${r}${g}${b}`;
  };

  const fillState = name => {
    let un = states[name];
    if (!un) return "white";
    let factor = un/maxUnemployment;
    let adjusted_factor = 1 + 1.25 * (factor - 1);
    return scaledColor(adjusted_factor < 0 ? 0 : adjusted_factor);
  };

  const fillLabel = name => {
    if (name === "Hawaii") return "black";
    let un = states[name];
    if (!un) return "black";
    return un/maxUnemployment > 0.6 ? "white" : "black";
  };

  const mouseout = () => displayTooltip = false;

  onMount(() => {
    let topoFetch = fetch('https://cdn.jsdelivr.net/npm/us-atlas@3/states-albers-10m.json').then(d => d.json());
    topoFetch.then((topo) => {
      stateGeo = feature(topo, topo.objects.states).features;
      try {
        let info = window.FNC.GEO.geo.get();
        let code = (info.countryCode === 'US') ? info.regionCode : null;
        if (code && abbr_to_state[code]) selectedState = abbr_to_state[code];
        else selectedState = "New York";
      } catch(e) {
        selectedState = "New York";
      }
      tick().then(SendHeight)
    })
  })
</script>

<div class="infographic infographic-covid-19" id="mapContainer">
  <header class="heading">
    <h1 class="title">Unemployment in the U.S.</h1>
    <h2 class="dek">{subtitle}</h2>
  </header>
  <div id="legend" class="legend">
    <div class="legend-min">0%</div>
    <div class="circles">
    {#each { length: numCircles} as _, i}
      <div class="circle" style={`background-color: ${scaledColor(i / (numCircles - 1))};`}></div>
    {/each}
    </div>
    <div class="legend-max">{maxUnemployment}%</div>
  </div>
  <div id="tooltip"
    class="info"
    class:tooltipLeft
    class:tooltipUp
    class:displayTooltip
    style={`left:${tooltipX}px; top:${tooltipY}px`}
  >
    <div class="title">{tooltipText}</div>
    <div class="value">{tooltipUnemployed}</div>
  </div>
  <div class="map-wrap" id="aspectRatioBox">
    <svg class="map" id="map" viewBox="{vb.x} {vb.y} {vb.w} {vb.h}">
      {#each stateGeo as d (d.id)}
        <path
          d={path(d)}
          class="state"
          stroke="#000"
          stroke-width="0.5"
          fill={fillState(d.properties.name)}
          on:mousemove={(e) => mousemove(e, d.properties.name)}
          on:mouseout={mouseout}
        />
      {/each}
      <g id="state-names" transform="translate(15 15) scale(1.08 1.15)">
        {#each state_symbols as s}
          <text
            dominant-baseline="hanging"
            text-anchor="middle"
            transform={s.transform}
            font-weight="600"
            font-size="13"
            fill={(states, fillLabel(abbr_to_state[s.symbol]))}
          >
            {s.symbol}
          </text>
        {/each}
      </g>
      {#each bubbles as bub, i}
        <g transform={`translate(${vb.x + vb.w - 30} ${vb.y + vb.h - 42 * (i + 1)})`}
           class="bubbles"
           on:mousemove={e => mousemove(e, bub.name)}
           on:mouseout={mouseout}
        >
          <circle
            fill={(states, fillState(bub.name))}
            stroke="#222"
            stroke-width="0.75"
            r="19"
            cx="0"
            cy="0"
          />
          <text
            dominant-baseline="middle"
            text-anchor="middle"
            font-weight="bold"
            dy="2"
            font-size="16"
            cursor="default"
            fill={(states, fillLabel(bub.name))}
          >
            {bub.label}
          </text>
        </g>
      {/each}
    </svg>
  </div>
  <div class="info-wrap">
      <div class="select-wrap">
        <select class="select state" bind:value={selectedState}>
          {#each stateNames as st}
            <option value={st}>{st}</option>
          {/each}
        </select>
      </div>
      <Card state={selectedState}
        rate={states && states[selectedState]}
      />
  </div>
  <div class="caption">
    <div class="source">Source: U.S. Bureau of Labor Statistics.</div>
    {#if lastUpdated}
      <div class="updated">Last Updated {lastUpdated}.</div>
    {/if}
  </div>
</div>

<style>
  .bubbles, #state-names {
    display: none;
  }
  .caption {
    color: #666;
    font-size: 12px;
    line-height: 18px;
  }
  .circles {
    display: flex;
    align-items: center;
  }
  .circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid #222;
    margin: 0 2px;
  }
  .legend {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    color: #222;
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
  }
  .legend-min {
    margin-right: 6px;
  }
  .legend-max {
    margin-left: 6px;
  }
  .map-wrap {
    position: relative;
    height: 0;
    overflow: hidden;
    width: 100%;
    padding-top: 68.4%;
    margin-bottom: 16px;
  }
  .map {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
  }
  .state:hover {
    stroke-width: 2.25;
  }
  .select-wrap.hidden {
    display: none;
  }
  .state {
    fill-opacity: 1.0;
    transition: fill-opacity 0.5s ease;
  }
  #tooltip {
    pointer-events: none;
    background: #fff;
    box-shadow: 0 0 8px 0 rgba(0,0,0,0.2);
    border-radius: 3px;
    opacity: 0;
    padding: 12px;
    position: absolute;
    transform: translate(0px, 0px);
    transition: transform 0.3s ease-in-out;
    min-width: 140px;
    z-index: 2;
  }
  #tooltip .title {
    font-weight: bold;
    margin-bottom: 5px;
  }
  #tooltip.tooltipLeft {
    transform: translateX(-110%);
  }
  #tooltip.tooltipUp {
    transform: translateY(-110%);
  }
  #tooltip.tooltipLeft.tooltipUp {
    transform: translate(-110%, -110%);
  }
  #tooltip.displayTooltip {
    opacity: 1.0;
  }
  #state-names {
    pointer-events: none;
  }

  @media (max-width: 325px) {
    .dek {
      display: none;
    }
  }

  @media (max-width: 686px) {
    #tooltip {
      display: none;
    }
    .state:hover {
      stroke-width: 0.5px;
    }
  }

  @media (min-width: 686px) {
    .bubbles, #state-names {
      display: unset;
    }
    .caption {
      margin-left: auto;
      margin-right: auto;
      max-width: 871px;
    }
    .heading {
      margin-bottom: 24px;
    }
    .heading .title {
      font-size: 30px;
      line-height: 36px;
      letter-spacing: -0.5px;
    }
    .legend {
      margin-bottom: 24px;
    }
  }
</style>
